"use client";
import { apiGet } from "@/utils/apiClient";
import React, { useEffect, useState } from "react";
import { BlogCard } from "../../app/buy-digital-gold/components/cards/BlogCard";
import { Post } from "@/type/commonTypes";
import HeadingSection from "../../app/buy-digital-gold/components/others/HeadingSection";
import Link from "next/link";
import Icon from "@/components/icons/Icons";
import {
  getReadingTime,
  getRelativeTime,
  lockBodyScroll,
  unlockBodyScroll,
} from "@/utils/commonFunctions";
import Image from "next/image";
import { AnimatePresence, motion } from "framer-motion";
import { BLOG_ROOT } from "@/utils/gloableVariables";

type News = {
  id: number;
  about: string;
  title: string;
  news: string;
  summary: string | null;
  news_url: string;
  publish_date: string;
  created_at: string;
  updated_at: string | null;
  status: number;
  image: string;
};

export default function BlogsAndNewsSection({
  keyWord,
  newsName,
  subgHeading,
}: {
  keyWord: string;
  newsName?: string;
  subgHeading?: string;
}) {
  const [blogsData, setBlogsData] = useState<Post[]>([]);
  const [newData, setNewsData] = useState<News[]>([]);
  const [newsLoaded, setNewsLoaded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedNews, setSelectedNews] = useState<News | null>(null);

  useEffect(() => {
    if (isOpen) {
      lockBodyScroll();
    } else {
      unlockBodyScroll();
    }
  }, [isOpen]);

  useEffect(() => {
    async function fetchBlogs() {
      try {
        const blogs = await apiGet(`/api/relatedBlogs?slug=${keyWord}`, { base: "none" });
        setBlogsData(blogs);
      } catch (error) {
        console.error(`Something went wrong -> ${error}`);
      }
    }

    fetchBlogs();
  }, []);

  useEffect(() => {
    async function fetchNews() {
      try {
        const data = await apiGet(`/api/dailyNews?type=${keyWord}s`, { base: "none", cache: "no-store" });

        setNewsData(data?.news ?? []);
      } catch (error) {
        console.error(`something went wrong --> ${error}`);
      } finally {
        setNewsLoaded(true);
      }
    }

    fetchNews();
  }, []);

  // Keep the news layout while loading so the blogs don't jump from 4 to 2 when news arrives
  const hasNews = !newsLoaded || newData.length > 0;
  const postsToRender = blogsData?.slice(0, hasNews ? 2 : 4);

  return (
    <>
      <div className="mb-10">
        <HeadingSection
          heading="Trending News and Blogs"
          subHeading={subgHeading ?? "Discover financial updates to guide your gold investment strategy."}
        />
      </div>

      <div className="flex flex-col-reverse sm:flex-row items-stretch gap-6">
        <div className={`bg-[#FFFFFF1A] rounded-6xl ${hasNews ? "" : "w-full min-w-0"}`}>
          <div className="p-4 sm:p-6 pb-0 sm:pb-0 flex items-center justify-between mb-6">
            <span className="text-white sm:text-xl font-semibold">Blogs and Updates</span>
            <Link href={BLOG_ROOT!} className="text-2sm text-white">
              See all
            </Link>
          </div>

          <div
            className={`p-4 sm:p-6 pt-0 sm:pt-0 flex items-center gap-4 sm:gap-6 overflow-x-auto scrollbar-none ${hasNews ? "" : "lg:grid lg:grid-cols-4 lg:items-stretch"}`}
          >
            {postsToRender.map((blog: Post) => {
              return (
                <BlogCard
                  key={blog.id}
                  post={blog}
                  className={`max-w-300 xs:max-w-82.5 ${hasNews ? "" : "lg:max-w-none"}`}
                />
              );
            })}
          </div>
        </div>

        {hasNews && (
          <div className="p-4 sm:p-6 bg-[#FFFFFF1A] rounded-6xl sm:max-w-77.5 lg:max-w-102.5">
            <div className="flex items-center justify-between mb-6">
              <span className="text-white sm:text-xl font-semibold">
                Trending News {newsName ? `on ${newsName}` : ""}
              </span>
              {/* <button aria-label="Refresh Button" className="group text-2sm text-white inline-flex items-center gap-px transition hover:text-[#ffffffc0]">
              <Icon.refreshIcon className="transition-all group-hover:rotate-180" /> Refresh
            </button> */}
            </div>

            <div className="space-y-6">
              {newData.map((item) => (
                <div
                  key={item.id}
                  onClick={() => {
                    setSelectedNews(item);
                    setIsOpen(true);
                  }}
                  className="flex space-x-4 cursor-pointer"
                >
                  <div className="w-23 h-16 sm:h-23 rounded-xs shrink-0 bg-[#333] overflow-hidden">
                    <Image
                      src={item.image}
                      alt={item.title}
                      width={92}
                      height={92}
                      className="w-full h-full object-contain"
                    />
                  </div>

                  <div className="flex flex-col justify-between">
                    <p className="text-white text-sm font-medium leading-snug line-clamp-2 sm:line-clamp-3">
                      {item.title}
                    </p>

                    <div className="flex items-center gap-2 text-xs text-gray-400 pt-1">
                      <span>{getRelativeTime(item.publish_date)}</span>
                      <span className="text-gray-500">•</span>
                      <span>{getReadingTime(item?.news || item?.summary || "")}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/70 backdrop-blur-2xl z-5050 flex justify-center items-start max-h-screen overflow-y-auto scrollbar-none "
              onClick={() => setIsOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="max-w-4xl text-white relative px-5 py-16 sm:py-20" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => setIsOpen(false)}
                  aria-label="Close Popup"
                  className="group absolute top-5 right-5 2md:right-0 w-7 h-7 sm:w-10 sm:h-10 bg-white rounded-full flex items-center justify-center"
                >
                  <Icon.popupCloseIcon className="w-4 h-4 sm:w-5 sm:h-5 text-primaryBlack group-hover:text-(--text) rotate-45 transition" />
                </button>

                <div className="font-semibold text-xl sm:text-2xl lg:text-3xl lg:leading-9 leading-1.3 sm:leading-1 mb-3">
                  {selectedNews?.title}
                </div>

                <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-400 pt-1 mb-8">
                  <span>{getRelativeTime(selectedNews?.publish_date || "")}</span>
                  <span className="text-gray-500">•</span>
                  <span>{getReadingTime(selectedNews?.news || selectedNews?.summary || "")}</span>
                </div>

                <div className="w-full rounded-8xl h-64 xs:h-80 sm:h-96 border-2 border-[#C2D9FF] overflow-hidden mb-8">
                  <Image
                    src={selectedNews?.image || ""}
                    alt={selectedNews?.title || ""}
                    width={900}
                    height={900}
                    className="w-full h-full object-contain object-center"
                  />
                </div>

                <div className="space-y-4 sm:space-y-6">
                  {(selectedNews?.news || selectedNews?.summary)?.split(".").map((sentence, index) =>
                    sentence.trim() ? (
                      <p key={index} className="font-light text-base sm:text-lg sm:leading-6.5 leading-1.5">
                        {sentence.trim()}.
                      </p>
                    ) : null,
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
